import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { hapticLight, hapticSuccess, hapticError } from '../../lib/mobile';
import { initPushNotifications, isNative } from '../../lib/mobile';
import {
  Cpu, Plus, Trash2, Edit2, Loader2, Save, Radio, Copy, RefreshCw,
  Thermometer, Droplet, Zap, Activity, ToggleLeft, X, Wifi, WifiOff,
  Settings, Flame, Lightbulb, Plug, MapPin, Sparkles, Play, Power
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;
const fmt = (n) => (Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 });

const KIND_META = {
  temp:         { icon: Thermometer, label: 'Sıcaklık (DHT)', unit: '°C', color: 'text-orange-600 bg-orange-100', gradient: 'from-orange-400 to-red-500' },
  temp_ds18b20: { icon: Thermometer, label: 'Sıcaklık (DS18B20)', unit: '°C', color: 'text-red-600 bg-red-100', gradient: 'from-red-400 to-rose-600' },
  humidity:     { icon: Droplet, label: 'Nem', unit: '%', color: 'text-blue-600 bg-blue-100', gradient: 'from-blue-400 to-cyan-500' },
  motion:       { icon: Activity, label: 'Hareket (PIR)', unit: '', color: 'text-violet-600 bg-violet-100', gradient: 'from-violet-400 to-purple-500' },
  analog:       { icon: Zap, label: 'Analog (LDR/Gaz)', unit: '', color: 'text-slate-600 bg-slate-100', gradient: 'from-slate-400 to-slate-600' },
  relay:        { icon: Power, label: 'Röle', unit: '', color: 'text-emerald-600 bg-emerald-100', gradient: 'from-emerald-400 to-teal-500' },
};

// ESP8266 (NodeMCU) → GPIO haritası
const ESP8266_PIN_MAP = {
  'D0': 'GPIO16 (⚠️ INPUT için pullup yok, PIR/röle önerilmez)',
  'D1': 'GPIO5 (SCL) - Röle için ideal',
  'D2': 'GPIO4 (SDA) - Röle için ideal',
  'D3': 'GPIO0 (⚠️ boot pin - PULL-UP zorunlu)',
  'D4': 'GPIO2 (LED_BUILTIN) - DHT için ideal',
  'D5': 'GPIO14 (SCK) - Röle/PIR uygun',
  'D6': 'GPIO12 (MISO) - Röle/PIR uygun',
  'D7': 'GPIO13 (MOSI) - Röle uygun',
  'D8': 'GPIO15 (⚠️ boot pin, pull-down zorunlu)',
  'A0': 'ADC (analog giriş, sadece 1 tane)',
};
const ESP32_PIN_MAP = {
  '2':  'GPIO2 (LED_BUILTIN)',
  '4':  'GPIO4 - DHT için ideal',
  '5':  'GPIO5 - Röle uygun',
  '13': 'GPIO13 - Röle uygun',
  '14': 'GPIO14 - Röle uygun',
  '25': 'GPIO25 - Röle uygun',
  '26': 'GPIO26 - Röle uygun',
  '27': 'GPIO27 - Röle uygun',
  '32': 'GPIO32 - Röle/sensör',
  '33': 'GPIO33 - Röle/sensör',
  '34': 'GPIO34 (sadece INPUT, pull-up yok)',
  '35': 'GPIO35 (sadece INPUT)',
};
// ESP32-S3 pin haritası (farklı GPIO numaraları, 1-21 güvenli)
const ESP32S3_PIN_MAP = {
  '1':  'GPIO1 - Giriş/çıkış (ADC1_CH0)',
  '2':  'GPIO2 - Giriş/çıkış',
  '4':  'GPIO4 - DHT için ideal',
  '5':  'GPIO5 - Röle uygun',
  '6':  'GPIO6 - Röle uygun',
  '7':  'GPIO7 - Röle uygun',
  '8':  'GPIO8 - Röle uygun',
  '9':  'GPIO9 - Röle/sensör',
  '10': 'GPIO10 - Röle/sensör',
  '11': 'GPIO11 - Röle uygun',
  '12': 'GPIO12 - Röle uygun',
  '13': 'GPIO13 - Röle uygun',
  '14': 'GPIO14 - Röle uygun',
  '15': 'GPIO15 - Röle uygun',
  '16': 'GPIO16 - Röle uygun',
  '17': 'GPIO17 - Röle uygun',
  '18': 'GPIO18 - Röle uygun',
  '21': 'GPIO21 - Röle uygun',
  '38': 'GPIO38 - Röle uygun',
  '39': 'GPIO39 - Röle uygun',
  '40': 'GPIO40 - Röle uygun',
  '41': 'GPIO41 - Röle uygun',
  '42': 'GPIO42 - Röle uygun',
};
// Arduino UNO + Ethernet Shield (W5100/W5500) pin haritası
// NOT: Ethernet Shield SPI için 10, 11, 12, 13 pinlerini kullanır (meşgul). Arduino UNO WiFi yoktur — Ethernet Shield gerekir (kablolu bağlantı).
const ARDUINO_UNO_PIN_MAP = {
  '2': 'D2 - Röle/sensör (SPI dışı, önerilir)',
  '3': 'D3 (PWM) - Röle/sensör',
  '4': 'D4 - Ethernet SD CS pini — sensör için dikkatli',
  '5': 'D5 (PWM) - Röle/sensör',
  '6': 'D6 (PWM) - Röle/sensör',
  '7': 'D7 - Röle/sensör önerilir',
  '8': 'D8 - Röle/sensör önerilir',
  '9': 'D9 (PWM) - Röle/sensör',
  'A0': 'Analog A0 (giriş)',
  'A1': 'Analog A1 (giriş)',
  'A2': 'Analog A2 (giriş)',
  'A3': 'Analog A3 (giriş)',
};

// Önerilen pin — DHT ve Röle için sıralı otomatik atama
function suggestPin(boardType, kind, direction, usedPins = []) {
  let priorities;
  if (boardType === 'esp32') {
    priorities = direction === 'out' ? ['5', '13', '14', '25', '26', '27'] : (kind === 'analog' ? ['32'] : ['4', '33', '2']);
  } else if (boardType === 'esp32s3') {
    priorities = direction === 'out' ? ['5', '6', '7', '8', '9', '10', '11', '12'] : (kind === 'analog' ? ['1'] : ['4', '2']);
  } else if (boardType === 'arduino_uno') {
    // Arduino Uno + Ethernet Shield: 10-13 shield için meşgul, 4 ise shield SD pini
    priorities = direction === 'out' ? ['7', '8', '9', '6', '5', '3'] : (kind === 'analog' ? ['A0', 'A1', 'A2', 'A3'] : ['2', '3', '5', '6']);
  } else {
    priorities = direction === 'out' ? ['D1', 'D2', 'D5', 'D6', 'D7'] : (kind === 'analog' ? ['A0'] : ['D4', 'D3']);
  }
  for (const p of priorities) {
    if (!usedPins.includes(p)) return p;
  }
  return priorities[0] || '';
}

const RELAY_PRESET_ICONS = {
  Kombi: Flame, Lamba: Lightbulb, Priz: Plug, Motor: Power, Klima: Sparkles,
};

const boardLabel = (bt) => {
  const t = (bt || '').toLowerCase();
  if (t === 'esp32s3') return 'ESP32-S3';
  if (t === 'esp32') return 'ESP32';
  if (t === 'esp8266') return 'ESP8266';
  if (t === 'arduino_uno') return 'Arduino UNO';
  return (bt || '').toUpperCase();
};

export default function HesapSmartPage() {
  const { token } = useHesapAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detailDev, setDetailDev] = useState(null);
  const [readings24h, setReadings24h] = useState([]);

  // Röle durumunu ekranda anında değiştir (sayfa yenilenmeden)
  const setRelayState = (deviceId, chId, state) => setDevices(list => list.map(d => (d.id !== deviceId ? d : {
    ...d, channels: (d.channels || []).map(c => (c.id === chId ? { ...c, state } : c)),
  })));

  // Sessiz yenile: loading flag'ı tetiklemez => "göz kırpması" olmaz
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [devR, redR] = await Promise.all([
        fetch(`${API}/smart/devices`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/smart/overview-readings?hours=24`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setDevices(devR);
      setReadings24h(redR);
    } finally { if (!silent) setLoading(false); }
  };

  // İlk yükleme (loading göster), sonrasında 5sn'de bir sessiz yenile
  // Modal açıkken polling'i durdur (kullanıcı ayar yaparken ekran yenilenmesin)
  useEffect(() => {
    load();
    const iv = setInterval(() => {
      if (!showAdd && !detailDev) load(true);
    }, 5000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [showAdd, detailDev]);

  // Mobil push bildirim kaydı (sadece Capacitor native ortamda)
  useEffect(() => {
    if (!isNative() || !token) return;
    initPushNotifications(API, token, (notif) => {
      toast.info(`🔔 ${notif.title || 'Bildirim'}: ${notif.body || ''}`);
    });
  }, [token]);

  const removeDevice = async (d) => {
    if (!window.confirm(`"${d.name}" cihazı ve tüm verileri silinsin mi?`)) return;
    const r = await fetch(`${API}/smart/devices/${d.id}`, { method: 'DELETE', headers });
    if (r.ok) { toast.success('Silindi'); load(); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-4" data-testid="hesap-smart-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Cpu className="h-6 w-6 text-emerald-600" />
            Akıllı Takip
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            ESP32 / ESP32-S3 / ESP8266 / Arduino UNO cihazları · Sensör okuma · Röle kontrolü · IP Kamera · Otomasyon
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="smart-add-device">
          <Plus className="h-4 w-4 mr-1" />Yeni Cihaz Ekle
        </Button>
      </div>

      {devices.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Cpu className="h-12 w-12 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Henüz cihaz eklenmedi.</p>
            <p className="text-xs text-slate-400 mt-1">
              Bir ESP32 veya ESP8266 kartı ekleyin, sensörler ve röleleri tanımlayın, sistem sizin için Arduino kodu üretsin.
            </p>
            <Button onClick={() => setShowAdd(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-1" />İlk Cihazımı Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (() => {
        // Özet kart: online/offline, toplam sensör/röle, aktif röle sayısı
        const onlineCount = devices.filter(d => d.online).length;
        const totalRelays = devices.reduce((s, d) => s + (d.channels || []).filter(c => c.direction === 'out').length, 0);
        const activeRelays = devices.reduce((s, d) => s + (d.channels || []).filter(c => c.direction === 'out' && c.state === 'on').length, 0);
        const allTemps = devices.flatMap(d => (d.channels || []).filter(c => c.kind === 'temp' || c.kind === 'temp_ds18b20').map(c => ({ device: d, ch: c, val: d.last_readings?.[c.id] })).filter(x => x.val != null));
        const avgTemp = allTemps.length ? allTemps.reduce((s, x) => s + x.val, 0) / allTemps.length : null;
        // Konuma göre grupla
        const grouped = devices.reduce((acc, d) => {
          const loc = d.location || 'Diğer';
          (acc[loc] = acc[loc] || []).push(d);
          return acc;
        }, {});
        return (
          <>
            {/* Üst grid: Hava widget + özetler */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <WeatherWidget indoorTemp={avgTemp} />
              <div className="grid grid-cols-2 gap-3 lg:col-span-2">
                <SummaryCard icon={Wifi} color="from-emerald-500 to-teal-500" label="Çevrimiçi Cihaz" value={`${onlineCount} / ${devices.length}`} />
                <SummaryCard icon={Thermometer} color="from-orange-500 to-red-500" label="Ortalama Sıcaklık" value={avgTemp != null ? `${fmt(avgTemp)}°C` : '—'} />
                <SummaryCard icon={Power} color="from-blue-500 to-cyan-500" label="Aktif Röle" value={`${activeRelays} / ${totalRelays}`} />
                <SummaryCard icon={Activity} color="from-violet-500 to-purple-500" label="Toplam Sensör" value={devices.reduce((s, d) => s + (d.channels || []).filter(c => c.direction === 'in').length, 0)} />
              </div>
            </div>

            {/* 24 saat sıcaklık özet kartları (her sıcaklık sensörü için ayrı) */}
            {(() => {
              const tempChannels = devices.flatMap(d => (d.channels || []).filter(c => c.kind === 'temp' || c.kind === 'temp_ds18b20').map(c => ({ device: d, ch: c })));
              if (tempChannels.length === 0) return null;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tempChannels.map(({ device: d, ch }) => (
                    <Temp24hCard key={`${d.id}-${ch.id}`} device={d} channel={ch} readings={readings24h.filter(r => r.device_id === d.id)} />
                  ))}
                </div>
              );
            })()}

            {/* Kameralı cihazlar - canlı görüntü + kapı aç */}
            {devices.some(d => (d.camera_url || '').trim() || ((d.camera_vendor && d.camera_vendor !== 'generic') && (d.camera_ip || '').trim())) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {devices.filter(d => (d.camera_url || '').trim() || ((d.camera_vendor && d.camera_vendor !== 'generic') && (d.camera_ip || '').trim())).map(d => (
                  <CameraCard key={`cam-${d.id}`} device={d} token={token} />
                ))}
              </div>
            )}

            {/* Konuma Göre Cihazlar */}
            {Object.entries(grouped).map(([location, devs]) => (
              <div key={location} className="space-y-2">
                <div className="flex items-center gap-2 mt-2">
                  <div className="h-6 w-1 bg-emerald-500 rounded" />
                  <h2 className="text-sm font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />{location}
                    <span className="text-xs font-normal text-slate-400">({devs.length} cihaz)</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {devs.map(d => <DeviceCard key={d.id} device={d} onOpen={() => setDetailDev(d)} onDelete={() => removeDevice(d)} token={token} onRefresh={() => load(true)} onRelayState={setRelayState} />)}
                </div>
              </div>
            ))}
          </>
        );
      })()}

      {showAdd && <AddDeviceDialog open={showAdd} onClose={() => setShowAdd(false)} onSaved={load} token={token} />}
      {detailDev && <DeviceDetailDialog device={detailDev} onClose={() => { setDetailDev(null); load(); }} token={token} />}
    </div>
  );
}

// -------------------- Hava Durumu Widget --------------------
function WeatherWidget({ indoorTemp }) {
  const [weather, setWeather] = useState(null);
  const [city, setCity] = useState(localStorage.getItem('smart_city') || 'İstanbul');
  const [editing, setEditing] = useState(false);
  const [coords, setCoords] = useState(() => {
    const c = localStorage.getItem('smart_coords');
    return c ? JSON.parse(c) : { lat: 41.015, lon: 28.979 }; // İstanbul default
  });

  const load = async () => {
    try {
      const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=temperature_2m_max,temperature_2m_min,weather_code&forecast_days=5&timezone=Europe/Istanbul`);
      if (r.ok) setWeather(await r.json());
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); const iv = setInterval(load, 300000); return () => clearInterval(iv); /* eslint-disable-next-line */ }, [coords.lat, coords.lon]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Konum servisi yok'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const newCoords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
      setCoords(newCoords);
      localStorage.setItem('smart_coords', JSON.stringify(newCoords));
      // Reverse geocode
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${newCoords.lat}&lon=${newCoords.lon}&zoom=10`);
        if (r.ok) {
          const d = await r.json();
          const cityName = d.address?.city || d.address?.town || d.address?.province || 'Konumum';
          setCity(cityName);
          localStorage.setItem('smart_city', cityName);
        }
      } catch { /* ignore */ }
      toast.success('Konum güncellendi');
    }, () => toast.error('Konum alınamadı'));
  };

  const weatherIcons = {
    0: '☀️', 1: '🌤️', 2: '⛅', 3: '☁️', 45: '🌫️', 48: '🌫️',
    51: '🌦️', 53: '🌦️', 55: '🌦️', 61: '🌧️', 63: '🌧️', 65: '🌧️',
    71: '🌨️', 73: '🌨️', 75: '❄️', 77: '❄️', 80: '🌦️', 81: '⛈️', 82: '⛈️',
    95: '⛈️', 96: '⛈️', 99: '⛈️',
  };
  const weatherLabels = {
    0: 'Açık', 1: 'Az bulutlu', 2: 'Parçalı bulutlu', 3: 'Bulutlu', 45: 'Sisli', 48: 'Sisli',
    51: 'Hafif çisenti', 53: 'Çisenti', 55: 'Yoğun çisenti', 61: 'Hafif yağmur', 63: 'Yağmur', 65: 'Şiddetli yağmur',
    71: 'Hafif kar', 73: 'Kar', 75: 'Yoğun kar', 80: 'Sağanak', 81: 'Sağanak', 82: 'Şiddetli sağanak',
    95: 'Fırtına', 96: 'Fırtına + dolu', 99: 'Şiddetli fırtına',
  };

  const current = weather?.current;
  const daily = weather?.daily;
  const wcode = current?.weather_code;
  const icon = weatherIcons[wcode] || '🌡️';
  const label = weatherLabels[wcode] || '—';

  return (
    <Card className="overflow-hidden bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 text-white border-0">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            {editing ? (
              <div className="flex gap-1 items-center">
                <Input value={city} onChange={e => setCity(e.target.value)} onBlur={() => { setEditing(false); localStorage.setItem('smart_city', city); }} onKeyDown={e => e.key === 'Enter' && (setEditing(false), localStorage.setItem('smart_city', city))} className="h-6 bg-white/20 text-white text-xs border-white/30" autoFocus />
              </div>
            ) : (
              <button onClick={() => setEditing(true)} className="text-lg font-bold flex items-center gap-1 hover:opacity-80" data-testid="weather-city">
                <MapPin className="h-4 w-4" />{city}
              </button>
            )}
            <p className="text-xs text-white/80">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <button onClick={useMyLocation} className="text-[10px] bg-white/20 hover:bg-white/30 px-2 py-1 rounded-full flex items-center gap-1" data-testid="weather-my-location">
            📍 Konumum
          </button>
        </div>
        {!current ? (
          <div className="flex items-center gap-3 py-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Hava durumu yükleniyor...</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="text-5xl">{icon}</div>
              <div>
                <p className="text-4xl font-bold leading-none">{fmt(current.temperature_2m)}°</p>
                <p className="text-xs text-white/90 mt-1">{label}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div className="bg-white/15 rounded px-2 py-1.5">
                <p className="text-[9px] text-white/70 uppercase">Nem</p>
                <p className="font-bold">{Math.round(current.relative_humidity_2m || 0)}%</p>
              </div>
              <div className="bg-white/15 rounded px-2 py-1.5">
                <p className="text-[9px] text-white/70 uppercase">Rüzgar</p>
                <p className="font-bold">{fmt(current.wind_speed_10m)} km/s</p>
              </div>
              <div className="bg-white/15 rounded px-2 py-1.5">
                <p className="text-[9px] text-white/70 uppercase">Min/Max</p>
                <p className="font-bold text-[11px]">{Math.round(daily?.temperature_2m_min?.[0] || 0)}° / {Math.round(daily?.temperature_2m_max?.[0] || 0)}°</p>
              </div>
            </div>
            {indoorTemp != null && (
              <div className="mt-2 bg-white/10 rounded px-2 py-1.5 flex items-center justify-between text-xs">
                <span className="text-white/80">🏠 İç ortam ortalama</span>
                <b>{fmt(indoorTemp)}°C</b>
              </div>
            )}
            {/* 5 Günlük Tahmin */}
            {daily?.temperature_2m_max?.length > 1 && (
              <div className="mt-2 border-t border-white/20 pt-2">
                <p className="text-[9px] text-white/70 uppercase mb-1">5 Günlük Tahmin</p>
                <div className="grid grid-cols-5 gap-1">
                  {daily.temperature_2m_max.slice(0, 5).map((max, i) => {
                    const dt = new Date(daily.time[i]);
                    const dayName = dt.toLocaleDateString('tr-TR', { weekday: 'short' });
                    const dcode = daily.weather_code?.[i];
                    const dicon = weatherIcons[dcode] || '🌡️';
                    return (
                      <div key={i} className="bg-white/10 rounded px-1 py-1 text-center">
                        <p className="text-[9px] text-white/80 capitalize">{i === 0 ? 'Bugün' : dayName}</p>
                        <p className="text-base leading-tight">{dicon}</p>
                        <p className="text-[10px] font-bold leading-tight">{Math.round(max)}°</p>
                        <p className="text-[9px] text-white/60 leading-tight">{Math.round(daily.temperature_2m_min[i])}°</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------- Özet Kartı --------------------
function SummaryCard({ icon: Icon, color, label, value }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------- 24 Saat Sıcaklık Kartı --------------------
function Temp24hCard({ device, channel, readings }) {
  const points = useMemo(() => {
    const arr = [];
    for (const r of readings) {
      const v = r.readings?.[channel.id];
      if (v != null && !isNaN(v)) {
        arr.push({ at: new Date(r.at).getTime(), v: Number(v), t: new Date(r.at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) });
      }
    }
    return arr;
  }, [readings, channel.id]);

  const current = device.last_readings?.[channel.id];
  const stats = useMemo(() => {
    if (points.length === 0) return null;
    const vals = points.map(p => p.v);
    const sum = vals.reduce((a, b) => a + b, 0);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const avg = sum / vals.length;
    const first = vals[0];
    const last = vals[vals.length - 1];
    return { avg, min, max, trend: last - first, count: vals.length };
  }, [points]);

  const unit = channel.unit || '°C';
  const trendUp = stats && stats.trend > 0.2;
  const trendDown = stats && stats.trend < -0.2;

  return (
    <Card className="overflow-hidden" data-testid={`temp24-card-${device.id}-${channel.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide flex items-center gap-1">
              <Thermometer className="h-3 w-3 text-orange-500" />
              {channel.label || 'Sıcaklık'} · 24 Saat
            </p>
            <p className="text-[10px] text-slate-400">{device.name} · {device.location || 'Konum yok'}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 leading-none">
              {current != null ? fmt(current) : '—'}<span className="text-sm text-slate-500">{unit}</span>
            </p>
            <p className="text-[10px] text-slate-500">Anlık</p>
          </div>
        </div>
        {stats ? (
          <>
            <div className="grid grid-cols-3 gap-1.5 mt-2">
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded px-2 py-1">
                <p className="text-[9px] text-slate-500 uppercase">Ortalama</p>
                <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{fmt(stats.avg)}{unit}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded px-2 py-1">
                <p className="text-[9px] text-slate-500 uppercase">En Düşük</p>
                <p className="text-sm font-bold text-cyan-600 dark:text-cyan-400">{fmt(stats.min)}{unit}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded px-2 py-1">
                <p className="text-[9px] text-slate-500 uppercase">En Yüksek</p>
                <p className="text-sm font-bold text-red-600 dark:text-red-400">{fmt(stats.max)}{unit}</p>
              </div>
            </div>
            <div className="mt-2 h-24">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={points} margin={{ top: 2, right: 4, left: 0, bottom: 0 }}>
                  <Line type="monotone" dataKey="v" stroke="#f97316" strokeWidth={1.5} dot={false} />
                  <YAxis hide domain={['dataMin - 1', 'dataMax + 1']} />
                  <XAxis dataKey="t" tick={{ fontSize: 9, fill: '#94a3b8' }} interval={Math.max(0, Math.floor(points.length / 6) - 1)} axisLine={false} tickLine={false} />
                  <Tooltip formatter={v => `${fmt(v)}${unit}`} labelFormatter={l => `Saat: ${l}`} contentStyle={{ fontSize: 10, padding: '2px 6px' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
              <span>{stats.count} ölçüm</span>
              <span className={trendUp ? 'text-red-500' : trendDown ? 'text-blue-500' : 'text-slate-500'}>
                {trendUp ? '↑ Yükseliş' : trendDown ? '↓ Düşüş' : '→ Sabit'} ({fmt(stats.trend)}{unit})
              </span>
            </div>
          </>
        ) : (
          <p className="text-center text-xs text-slate-400 py-6">24 saatlik veri henüz yok.<br /><span className="text-[10px]">Cihaz ilk telemetriyi gönderdiğinde burada görünecek.</span></p>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------- IP Kamera Kartı (dashboard'da) --------------------
function CameraCard({ device, token }) {
  const [tick, setTick] = useState(0);
  const [opening, setOpening] = useState(false);
  const [errored, setErrored] = useState(false);
  const camType = device.camera_type || 'snapshot';
  const hasBackendCamera = !!(device.camera_vendor && device.camera_vendor !== 'generic' && device.camera_ip) || !!(device.camera_url || '').trim();
  const doorRelay = (device.channels || []).find(c => c.id === device.door_relay_id && c.direction === 'out');

  // Backend proxy URL'leri (auth token query param olarak eklenir)
  const base = `${API}/smart/devices/${device.id}`;
  const snapUrl = `${base}/camera-snapshot?_t=${tick}&token=${encodeURIComponent(token)}`;
  const streamUrl = `${base}/camera-stream?token=${encodeURIComponent(token)}`;

  // Snapshot modu veya MJPEG stream dışında herhangi bir mod → snapshot proxy'yi N saniyede bir yenile
  useEffect(() => {
    if (!hasBackendCamera) return;
    if (camType === 'mjpeg') return;  // MJPEG stream kendi akar
    const iv = setInterval(() => { setTick(t => t + 1); setErrored(false); }, camType === 'snapshot' ? 2000 : 5000);
    return () => clearInterval(iv);
  }, [camType, hasBackendCamera]);

  const openDoor = async () => {
    setOpening(true);
    hapticLight();
    try {
      const r = await fetch(`${API}/smart/devices/${device.id}/door-open`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (r.ok) {
        const d = await r.json();
        hapticSuccess();
        toast.success(`🚪 Kapı açılıyor (${(d.pulse_ms || 3000) / 1000}sn)...`);
      } else {
        const d = await r.json().catch(() => ({}));
        hapticError();
        toast.error(d.detail || 'Kapı açılamadı');
      }
    } catch { hapticError(); toast.error('Kapı açılamadı'); }
    finally { setOpening(false); }
  };

  const imageSrc = camType === 'mjpeg' ? streamUrl : snapUrl;

  return (
    <Card className="overflow-hidden" data-testid={`camera-card-${device.id}`}>
      <div className="relative bg-slate-900 aspect-video overflow-hidden">
        {!hasBackendCamera ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-xs">
            Kamera tanımlanmamış
          </div>
        ) : errored ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-300 text-center text-xs p-3">
            <WifiOff className="h-8 w-8 mb-2 text-amber-400" />
            <p>Kameraya bağlanılamadı.</p>
            <p className="text-[10px] text-slate-500 mt-1">IP, kullanıcı/parola ve kanal bilgisini kontrol edin.</p>
          </div>
        ) : (
          <img
            src={imageSrc}
            alt={device.camera_label || device.name}
            className="w-full h-full object-cover"
            onError={() => setErrored(true)}
            onLoad={() => setErrored(false)}
          />
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> CANLI
        </div>
        <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded uppercase">
          {(device.camera_vendor || 'generic')} · {camType}
        </div>
      </div>
      <CardContent className="p-2.5">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{device.camera_label || 'Kamera'}</p>
            <p className="text-[10px] text-slate-500 truncate">{device.name} · {device.location || 'Konum yok'}</p>
          </div>
          {doorRelay && (
            <Button
              onClick={openDoor}
              disabled={opening || !device.online}
              className="bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white h-9 shadow-md"
              data-testid={`door-open-${device.id}`}
            >
              {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <>🚪 Kapıyı Aç</>}
            </Button>
          )}
        </div>
        {doorRelay && !device.online && (
          <p className="text-[10px] text-amber-600 mt-1">⚠️ Cihaz çevrimdışı, kapı açılamayabilir.</p>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------- Cihaz Kartı --------------------
function DeviceCard({ device, onOpen, onDelete, token, onRefresh, onRelayState }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const sensors = (device.channels || []).filter(c => c.direction === 'in');
  const relays = (device.channels || []).filter(c => c.direction === 'out');

  const toggleRelay = async (ch, action) => {
    hapticLight();
    const prev = ch.state;
    onRelayState(device.id, ch.id, action);
    try {
      const r = await fetch(`${API}/smart/devices/${device.id}/toggle/${ch.id}`, {
        method: 'POST', headers, body: JSON.stringify({ action })
      });
      if (!r.ok) throw new Error();
      hapticSuccess();
      toast.success(`${ch.label}: ${action === 'on' ? 'AÇ' : 'KAPAT'} komutu gönderildi`, { duration: 1500 });
      onRefresh();
    } catch {
      hapticError();
      onRelayState(device.id, ch.id, prev);
      toast.error(`${ch.label}: komut gönderilemedi`);
    }
  };

  return (
    <Card className={device.online ? 'border-emerald-300' : 'border-slate-200'}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${device.online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              {device.online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-sm truncate">{device.name}</CardTitle>
              <p className="text-[10px] text-slate-500 flex items-center gap-1">
                <MapPin className="h-2.5 w-2.5" />{device.location || 'Konum yok'} · {boardLabel(device.board_type)}
              </p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onOpen} data-testid={`open-device-${device.id}`}><Settings className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={onDelete}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {/* Sensörler */}
        {sensors.length > 0 && (
          <div className="grid grid-cols-2 gap-1.5">
            {sensors.map(ch => {
              const meta = KIND_META[ch.kind] || KIND_META.analog;
              const Icon = meta.icon;
              const val = device.last_readings?.[ch.id];
              return (
                <div key={ch.id} className="bg-slate-50 dark:bg-slate-800/40 rounded px-2 py-1.5 flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded ${meta.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-3 w-3" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] text-slate-500 truncate">{ch.label || meta.label}</p>
                    <p className="text-xs font-semibold">{val != null ? `${fmt(val)}${ch.unit || meta.unit}` : '—'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Röleler */}
        {relays.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {relays.map(ch => {
              const PresetIcon = RELAY_PRESET_ICONS[ch.label] || Power;
              const isOn = ch.state === 'on';
              return (
                <button
                  key={ch.id}
                  onClick={() => toggleRelay(ch, isOn ? 'off' : 'on')}
                  className={`relative rounded-xl p-3 border-2 transition-all overflow-hidden ${isOn ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 shadow-md shadow-emerald-100 dark:shadow-emerald-900/40' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 hover:border-slate-300'}`}
                  data-testid={`toggle-${ch.id}`}
                >
                  {isOn && (
                    <div className="absolute top-1 right-1">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                    </div>
                  )}
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center transition-all ${isOn ? 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-lg' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                    <PresetIcon className="h-5 w-5" />
                  </div>
                  <p className="text-xs font-semibold text-center mt-2 truncate">{ch.label || 'Röle'}</p>
                  <p className={`text-[10px] text-center font-bold uppercase tracking-wider ${isOn ? 'text-emerald-600' : 'text-slate-400'}`}>{isOn ? 'AÇIK' : 'KAPALI'}</p>
                  <p className="text-[9px] text-slate-400 text-center">Pin {ch.pin || '?'}</p>
                </button>
              );
            })}
          </div>
        )}
        <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700">
          {device.online ? `● Çevrimiçi · ${device.seconds_ago ?? 0}sn önce` : `○ Çevrimdışı${device.seconds_ago != null ? ` (${Math.floor(device.seconds_ago / 60)}dk)` : ''}`}
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------- Cihaz Ekle Dialog --------------------
function AddDeviceDialog({ open, onClose, onSaved, token }) {
  const [form, setForm] = useState({ name: '', board_type: 'esp8266', location: '', threshold_seconds: 180, channels: [] });
  const [saving, setSaving] = useState(false);

  const addChannel = (direction, kind) => {
    const meta = KIND_META[kind];
    const usedPins = form.channels.filter(c => c.direction === direction).map(c => c.pin);
    const pin = suggestPin(form.board_type, kind, direction, usedPins);
    setForm(f => ({
      ...f,
      channels: [...f.channels, {
        pin,
        direction, kind,
        label: meta?.label || '',
        unit: meta?.unit || '',
        dht_type: (kind === 'temp' || kind === 'humidity') ? 'DHT22' : '',
        active_low: kind === 'relay' ? true : false,
      }]
    }));
  };

  const removeChannel = (idx) => setForm(f => ({ ...f, channels: f.channels.filter((_, i) => i !== idx) }));
  const updateChannel = (idx, patch) => setForm(f => ({ ...f, channels: f.channels.map((c, i) => i === idx ? { ...c, ...patch } : c) }));

  // Hazır şablonlar
  const templates = [
    { name: 'Kombi Odası', channels: [
      { direction: 'in', kind: 'temp', label: 'Oda Sıcaklığı' },
      { direction: 'in', kind: 'humidity', label: 'Nem' },
      { direction: 'out', kind: 'relay', label: 'Kombi' },
    ]},
    { name: 'Salon Otomasyonu', channels: [
      { direction: 'in', kind: 'temp', label: 'Salon Sıcaklığı' },
      { direction: 'in', kind: 'motion', label: 'Hareket' },
      { direction: 'out', kind: 'relay', label: 'Lamba' },
    ]},
    { name: '4\'lü Röle Kartı', channels: [
      { direction: 'out', kind: 'relay', label: 'Röle 1' },
      { direction: 'out', kind: 'relay', label: 'Röle 2' },
      { direction: 'out', kind: 'relay', label: 'Röle 3' },
      { direction: 'out', kind: 'relay', label: 'Röle 4' },
    ]},
    { name: '8\'li Röle Kartı', channels: Array.from({ length: 8 }, (_, i) => ({ direction: 'out', kind: 'relay', label: `Röle ${i + 1}` }))},
  ];

  const applyTemplate = (tpl) => {
    setForm(f => {
      const startChannels = [...f.channels];
      // Her yeni kanal için farklı pin ataması yap
      const finalChannels = [];
      const combined = [...startChannels];
      for (const c of tpl.channels) {
        const usedPins = combined.filter(x => x.direction === c.direction).map(x => x.pin);
        const newCh = {
          ...c,
          unit: KIND_META[c.kind]?.unit || '',
          pin: suggestPin(f.board_type, c.kind, c.direction, usedPins),
          dht_type: (c.kind === 'temp' || c.kind === 'humidity') ? 'DHT22' : '',
          active_low: c.kind === 'relay' ? true : false,
        };
        combined.push(newCh);
        finalChannels.push(newCh);
      }
      return { ...f, channels: [...startChannels, ...finalChannels] };
    });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Cihaz adı gerekli'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/smart/devices`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Kaydedilemedi'); }
      toast.success('Cihaz eklendi. Şimdi ayarlar bölümünden Arduino kodunu alabilirsiniz.');
      onSaved(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Cpu className="h-5 w-5 text-emerald-600" />Yeni Akıllı Cihaz</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Cihaz Adı</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Salon Kombi Kontrol" data-testid="dev-name" />
            </div>
            <div>
              <Label>Konum</Label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Salon / Mutfak / İşyeri" />
            </div>
            <div>
              <Label>Kart Tipi</Label>
              <Select value={form.board_type} onValueChange={v => setForm({ ...form, board_type: v })}>
                <SelectTrigger data-testid="dev-board"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="esp32">ESP32 (WiFi)</SelectItem>
                  <SelectItem value="esp32s3">ESP32-S3 (WiFi)</SelectItem>
                  <SelectItem value="esp8266">NodeMCU / ESP8266 (WiFi)</SelectItem>
                  <SelectItem value="arduino_uno">Arduino UNO + Ethernet Shield (Kablolu)</SelectItem>
                </SelectContent>
              </Select>
              {form.board_type === 'arduino_uno' && (
                <p className="text-[10px] text-amber-600 mt-0.5">⚠️ Arduino UNO'nun WiFi'si yoktur, W5100/W5500 Ethernet Shield gerekir.</p>
              )}
            </div>
            <div>
              <Label>Sinyal Eşiği (sn)</Label>
              <Input type="number" min="30" value={form.threshold_seconds} onChange={e => setForm({ ...form, threshold_seconds: e.target.value })} />
            </div>
          </div>

          {/* Hazır şablonlar */}
          <div>
            <Label className="text-xs text-slate-500">Hızlı Şablonlar (kanal ekler)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {templates.map((tpl, i) => (
                <button key={i} onClick={() => applyTemplate(tpl)} className="text-xs bg-slate-100 hover:bg-emerald-100 dark:bg-slate-800 dark:hover:bg-emerald-900/40 px-2 py-1 rounded border border-slate-200 dark:border-slate-700" data-testid={`tpl-${i}`}>
                  + {tpl.name}
                </button>
              ))}
            </div>
          </div>

          {/* Kanallar */}
          <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/40">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold">Kanallar ({form.channels.length})</Label>
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="outline" onClick={() => addChannel('in', 'temp')} className="h-7 text-xs"><Thermometer className="h-3 w-3 mr-1" />DHT</Button>
                <Button size="sm" variant="outline" onClick={() => addChannel('in', 'temp_ds18b20')} className="h-7 text-xs"><Thermometer className="h-3 w-3 mr-1" />DS18B20</Button>
                <Button size="sm" variant="outline" onClick={() => addChannel('in', 'humidity')} className="h-7 text-xs"><Droplet className="h-3 w-3 mr-1" />Nem</Button>
                <Button size="sm" variant="outline" onClick={() => addChannel('in', 'motion')} className="h-7 text-xs"><Activity className="h-3 w-3 mr-1" />PIR</Button>
                <Button size="sm" variant="outline" onClick={() => addChannel('in', 'analog')} className="h-7 text-xs"><Zap className="h-3 w-3 mr-1" />Analog</Button>
                <Button size="sm" variant="outline" onClick={() => addChannel('out', 'relay')} className="h-7 text-xs text-emerald-700"><Power className="h-3 w-3 mr-1" />Röle</Button>
              </div>
            </div>
            {form.channels.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-3">Yukarıdaki butonlarla veya şablonlarla kanal ekleyin</p>
            ) : (
              <div className="space-y-1">
                {form.channels.map((ch, i) => {
                  const meta = KIND_META[ch.kind] || {};
                  const Icon = meta.icon || Radio;
                  const isDHT = ch.kind === 'temp' || ch.kind === 'humidity';
                  const isRelay = ch.kind === 'relay';
                  return (
                    <div key={i} className="bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 p-2 space-y-1">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className={`col-span-1 w-6 h-6 rounded ${meta.color || ''} flex items-center justify-center`}><Icon className="h-3 w-3" /></div>
                        <Input className="col-span-4 h-7 text-xs" value={ch.label} onChange={e => updateChannel(i, { label: e.target.value })} placeholder="Etiket (örn Kombi)" />
                        <Input className="col-span-2 h-7 text-xs font-mono" value={ch.pin} onChange={e => updateChannel(i, { pin: e.target.value })} placeholder="Pin" />
                        <div className="col-span-4 text-[10px] text-slate-500">{ch.direction === 'in' ? '📥 ' : '📤 '}{meta.label}</div>
                        <Button size="icon" variant="ghost" className="col-span-1 h-7 w-7 text-red-500" onClick={() => removeChannel(i)}><X className="h-3.5 w-3.5" /></Button>
                      </div>
                      {(isDHT || isRelay) && (
                        <div className="grid grid-cols-12 gap-2 items-center pl-8">
                          {isDHT && (
                            <>
                              <span className="col-span-2 text-[10px] text-slate-500">DHT Tipi:</span>
                              <Select value={ch.dht_type || 'DHT22'} onValueChange={v => updateChannel(i, { dht_type: v })}>
                                <SelectTrigger className="col-span-3 h-6 text-[10px]" data-testid={`dht-type-${i}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="DHT11">DHT11 (mavi, ucuz)</SelectItem>
                                  <SelectItem value="DHT22">DHT22 (beyaz, hassas)</SelectItem>
                                </SelectContent>
                              </Select>
                            </>
                          )}
                          {isRelay && (
                            <>
                              <span className="col-span-2 text-[10px] text-slate-500">Röle tipi:</span>
                              <Select value={ch.active_low ? 'low' : 'high'} onValueChange={v => updateChannel(i, { active_low: v === 'low' })}>
                                <SelectTrigger className="col-span-4 h-6 text-[10px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Active LOW (LOW=AÇIK) — yaygın</SelectItem>
                                  <SelectItem value="high">Active HIGH (HIGH=AÇIK)</SelectItem>
                                </SelectContent>
                              </Select>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>İptal</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dev-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Kaydet</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Cihaz Detay Dialog --------------------
function DeviceDetailDialog({ device: initial, onClose, token }) {
  const [device, setDevice] = useState(initial);
  const [rules, setRules] = useState([]);
  const [readings, setReadings] = useState([]);
  const [tab, setTab] = useState('overview'); // overview | rules | code
  const [showKey, setShowKey] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: '', trigger: 'sensor', sensor_channel: '', op: '<', value: 20, action_channel: '', action: 'on' });
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async (silent = false) => {
    const [devR, rulR, redR] = await Promise.all([
      fetch(`${API}/smart/devices/${device.id}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/smart/devices/${device.id}/rules`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/smart/devices/${device.id}/readings?hours=24`, { headers }).then(r => r.ok ? r.json() : []),
    ]);
    if (devR) setDevice(devR);
    setRules(rulR); setReadings(redR);
  };

  // Sessiz polling: Settings/Code sekmesinde kullanıcı veri girerken polling'i durdur
  useEffect(() => {
    load();
    const iv = setInterval(() => {
      if (tab === 'overview' || tab === 'rules') load(true);
    }, 3000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [tab]);

  const sensors = (device.channels || []).filter(c => c.direction === 'in');
  const relays = (device.channels || []).filter(c => c.direction === 'out');

  const chartData = useMemo(() => readings.map(r => {
    const row = { time: new Date(r.at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) };
    Object.entries(r.readings || {}).forEach(([chId, v]) => {
      const ch = sensors.find(s => s.id === chId);
      if (ch) row[ch.label || chId] = v;
    });
    return row;
  }), [readings, sensors]);

  const toggleRelay = async (ch, action) => {
    hapticLight();
    const setState = (state) => setDevice(d => ({ ...d, channels: (d.channels || []).map(c => (c.id === ch.id ? { ...c, state } : c)) }));
    const prev = ch.state;
    setState(action);
    try {
      const r = await fetch(`${API}/smart/devices/${device.id}/toggle/${ch.id}`, { method: 'POST', headers, body: JSON.stringify({ action }) });
      if (!r.ok) throw new Error();
      hapticSuccess();
      toast.success(`${ch.label}: ${action === 'on' ? 'AÇ' : 'KAPAT'} komutu gönderildi`, { duration: 1500 });
      load(true);
    } catch {
      hapticError();
      setState(prev);
      toast.error(`${ch.label}: komut gönderilemedi`);
    }
  };

  const rotateKey = async () => {
    if (!window.confirm('Yeni API key oluşturulacak. Cihaz güncellenene kadar bağlantı kesilir. Devam?')) return;
    const r = await fetch(`${API}/smart/devices/${device.id}/rotate-key`, { method: 'POST', headers });
    if (r.ok) { setDevice(await r.json()); toast.success('API key yenilendi'); }
  };

  const saveRule = async () => {
    if (!ruleForm.sensor_channel && ruleForm.trigger === 'sensor') { toast.error('Sensör seçin'); return; }
    if (!ruleForm.action_channel) { toast.error('Röle seçin'); return; }
    const r = await fetch(`${API}/smart/devices/${device.id}/rules`, { method: 'POST', headers, body: JSON.stringify(ruleForm) });
    if (r.ok) {
      toast.success('Kural eklendi');
      setRuleForm({ name: '', trigger: 'sensor', sensor_channel: '', op: '<', value: 20, action_channel: '', action: 'on' });
      load();
    }
  };

  const deleteRule = async (id) => {
    const r = await fetch(`${API}/smart/rules/${id}`, { method: 'DELETE', headers });
    if (r.ok) { toast.success('Silindi'); load(); }
  };

  const copy = (txt, msg = 'Kopyalandı') => { navigator.clipboard?.writeText(txt); toast.success(msg); };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-emerald-600" />{device.name}
            {device.online ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">🟢 Çevrimiçi</span> : <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">⚫ Çevrimdışı</span>}
          </DialogTitle>
        </DialogHeader>
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700 -mt-2 overflow-x-auto">
          {[
            { k: 'overview', label: 'Anlık Durum', icon: Activity },
            { k: 'settings', label: 'Ayarlar', icon: Settings },
            { k: 'rules', label: `Kurallar (${rules.length})`, icon: Sparkles },
            { k: 'code', label: 'Arduino Kod', icon: Cpu },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-1.5 text-sm font-medium border-b-2 flex items-center gap-1 whitespace-nowrap ${tab === t.k ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`tab-${t.k}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-3">
            {sensors.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Sensörler</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                  {sensors.map(ch => {
                    const meta = KIND_META[ch.kind] || KIND_META.analog;
                    const Icon = meta.icon;
                    const val = device.last_readings?.[ch.id];
                    return (
                      <div key={ch.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                        <div className={`w-8 h-8 rounded ${meta.color} flex items-center justify-center mb-1`}><Icon className="h-4 w-4" /></div>
                        <p className="text-[10px] text-slate-500 truncate">{ch.label}</p>
                        <p className="text-lg font-bold">{val != null ? `${fmt(val)}${ch.unit || meta.unit}` : '—'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {relays.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Röleler / Aktüatörler</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                  {relays.map(ch => {
                    const PresetIcon = RELAY_PRESET_ICONS[ch.label] || Power;
                    const isOn = ch.state === 'on';
                    return (
                      <div key={ch.id} className={`border-2 rounded-lg p-2 transition-colors ${isOn ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                        <div className="flex items-center justify-between">
                          <PresetIcon className={`h-5 w-5 ${isOn ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <Switch checked={isOn} onCheckedChange={(v) => toggleRelay(ch, v ? 'on' : 'off')} data-testid={`detail-toggle-${ch.id}`} />
                        </div>
                        <p className="text-xs font-semibold mt-2">{ch.label}</p>
                        <p className="text-[10px] text-slate-500">Pin {ch.pin} · {isOn ? 'AÇIK' : 'KAPALI'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {chartData.length > 0 && sensors.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500">Son 24 Saat</Label>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    {sensors.map((s, i) => (
                      <Line key={s.id} type="monotone" dataKey={s.label || s.id} stroke={['#ea580c', '#3b82f6', '#8b5cf6', '#10b981'][i % 4]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Settings — Kanalları düzenle */}
        {tab === 'settings' && (
          <SettingsTab device={device} setDevice={setDevice} onSaved={load} token={token} />
        )}

        {/* Rules */}
        {tab === 'rules' && (
          <div className="space-y-3">
            <div className="border rounded-lg p-3 bg-emerald-50/40 dark:bg-emerald-900/10">
              <Label className="font-semibold text-emerald-800 dark:text-emerald-200">➕ Yeni Otomasyon Kuralı</Label>
              <div className="grid grid-cols-12 gap-2 mt-2">
                <Input className="col-span-6 h-8 text-xs" placeholder="Kural adı (örn: Sıcaklık düşünce kombiyi aç)" value={ruleForm.name} onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })} />
                <Select value={ruleForm.trigger} onValueChange={v => setRuleForm({ ...ruleForm, trigger: v })}>
                  <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sensor">Sensör Tetikleyici</SelectItem>
                  </SelectContent>
                </Select>
                <div className="col-span-3 text-[10px] text-slate-500 flex items-end">Sensör değeri koşula uyduğunda röle çalışır</div>
              </div>
              <div className="grid grid-cols-12 gap-2 mt-2">
                <Select value={ruleForm.sensor_channel} onValueChange={v => setRuleForm({ ...ruleForm, sensor_channel: v })}>
                  <SelectTrigger className="col-span-4 h-8 text-xs"><SelectValue placeholder="Sensör seç" /></SelectTrigger>
                  <SelectContent>
                    {sensors.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={ruleForm.op} onValueChange={v => setRuleForm({ ...ruleForm, op: v })}>
                  <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="<">&lt;</SelectItem>
                    <SelectItem value="<=">≤</SelectItem>
                    <SelectItem value=">">&gt;</SelectItem>
                    <SelectItem value=">=">≥</SelectItem>
                    <SelectItem value="==">=</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" step="0.1" className="col-span-2 h-8 text-xs" value={ruleForm.value} onChange={e => setRuleForm({ ...ruleForm, value: e.target.value })} />
                <div className="col-span-1 text-center text-xs pt-2">→</div>
                <Select value={ruleForm.action_channel} onValueChange={v => setRuleForm({ ...ruleForm, action_channel: v })}>
                  <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue placeholder="Röle" /></SelectTrigger>
                  <SelectContent>
                    {relays.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={ruleForm.action} onValueChange={v => setRuleForm({ ...ruleForm, action: v })}>
                  <SelectTrigger className="col-span-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on">AÇ</SelectItem>
                    <SelectItem value="off">KAPAT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={saveRule} className="bg-emerald-600 hover:bg-emerald-700 h-7" data-testid="rule-save"><Plus className="h-3.5 w-3.5 mr-1" />Kural Ekle</Button>
              </div>
            </div>
            {rules.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-4">Henüz kural yok.</p>
            ) : (
              <div className="space-y-1">
                {rules.map(rule => {
                  const s = sensors.find(x => x.id === rule.sensor_channel);
                  const rel = relays.find(x => x.id === rule.action_channel);
                  return (
                    <div key={rule.id} className="border rounded-lg p-2 flex items-center justify-between">
                      <div className="text-sm">
                        <b>{rule.name}</b> · <span className="text-xs text-slate-500">
                          {s?.label || rule.sensor_channel} {rule.op} {rule.value}{s?.unit} → {rel?.label} <b className={rule.action === 'on' ? 'text-emerald-700' : 'text-red-600'}>{rule.action.toUpperCase()}</b>
                        </span>
                        {rule.last_triggered_at && <span className="text-[10px] text-slate-400 ml-2">Son tetik: {new Date(rule.last_triggered_at).toLocaleString('tr-TR')}</span>}
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteRule(rule.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Code Generator */}
        {tab === 'code' && (() => {
          const baseUrl = (device.public_base_url || RUNTIME_BACKEND_URL || window.location.origin).replace(/\/$/, '');
          const telemetryUrl = `${baseUrl}/api/hesap/smart/telemetry`;
          const pollUrl = `${baseUrl}/api/hesap/smart/poll?api_key=${device.api_key}`;
          const ackUrl = `${baseUrl}/api/hesap/smart/ack`;
          const isHttps = telemetryUrl.startsWith('https://');
          const boardType = device.board_type || 'esp32';
          const isESP32 = boardType === 'esp32' || boardType === 'esp32s3';
          const code = generateArduinoCode(device, { telemetryUrl, pollUrl, ackUrl, isHttps, isESP32, boardType });
          return (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">API Key</Label>
                  <div className="flex gap-1">
                    <Input type={showKey ? 'text' : 'password'} value={device.api_key} readOnly className="font-mono text-xs h-8" />
                    <Button size="sm" variant="outline" onClick={() => setShowKey(v => !v)} className="h-8">{showKey ? '👁️' : '🙈'}</Button>
                    <Button size="sm" variant="outline" onClick={() => copy(device.api_key, 'Key kopyalandı')} className="h-8"><Copy className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={rotateKey} className="h-8 text-red-600"><RefreshCw className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Public URL</Label>
                  <div className="flex gap-1">
                    <Input value={device.public_base_url || ''} onChange={e => setDevice({ ...device, public_base_url: e.target.value })} placeholder={window.location.origin} className="text-xs h-8 font-mono" />
                    <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={async () => {
                      const r = await fetch(`${API}/smart/devices/${device.id}`, { method: 'PUT', headers, body: JSON.stringify({ public_base_url: (device.public_base_url || '').trim() }) });
                      if (r.ok) { setDevice(await r.json()); toast.success('Kaydedildi'); }
                    }}><Save className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Otomatik Üretilen Arduino Kodu ({boardLabel(device.board_type)})</Label>
                  <Button size="sm" variant="outline" onClick={() => copy(code, 'Kod kopyalandı')} className="h-7"><Copy className="h-3.5 w-3.5 mr-1" />Kopyala</Button>
                </div>
                <pre className="bg-slate-900 text-emerald-100 text-[10px] p-3 rounded overflow-x-auto font-mono leading-relaxed max-h-96" data-testid="smart-code">{code}</pre>
              </div>

              {/* Pin Bağlantı Haritası */}
              {(device.channels || []).some(c => !c.pin) && (
                <div className="bg-amber-50 dark:bg-amber-900/30 border-l-4 border-amber-500 rounded p-2 text-xs text-amber-800 dark:text-amber-200">
                  ⚠️ Bazı kanalların pini <b>atanmamış</b>. Lütfen <b>Ayarlar</b> sekmesine gidip her kanal için bir pin seçin, aksi halde Arduino kodu yüklendiğinde çalışmaz.
                </div>
              )}
              <div className="border border-emerald-200 dark:border-emerald-800 rounded-lg overflow-hidden">
                <div className="bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-xs font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
                  🔌 Fiziksel Bağlantı Haritası — Kabloları hangi pine bağlayacağınız
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-[10px]">
                    <tr>
                      <th className="text-left p-2">Cihaz / Kanal</th>
                      <th className="text-left p-2">Pin</th>
                      <th className="text-left p-2">Detay</th>
                      <th className="text-left p-2 hidden sm:table-cell">Kablo Bağlantısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(device.channels || []).map(ch => {
                      const meta = KIND_META[ch.kind] || {};
                      const Icon = meta.icon || Radio;
                      const pinMap = device.board_type === 'esp32' ? ESP32_PIN_MAP
                        : device.board_type === 'esp32s3' ? ESP32S3_PIN_MAP
                        : device.board_type === 'arduino_uno' ? ARDUINO_UNO_PIN_MAP
                        : ESP8266_PIN_MAP;
                      const pinDetail = pinMap[ch.pin] || '-';
                      const wire = ch.kind === 'temp' || ch.kind === 'humidity'
                        ? `${ch.dht_type} → VCC=3.3V/5V, GND=GND, DATA=Pin ${ch.pin}`
                        : ch.kind === 'temp_ds18b20'
                        ? `DS18B20 → VCC=3.3V, GND=GND, DATA=Pin ${ch.pin} (+ 4.7kΩ pull-up DATA↔VCC)`
                        : ch.kind === 'motion'
                        ? `PIR → VCC=5V, GND=GND, OUT=Pin ${ch.pin}`
                        : ch.kind === 'analog'
                        ? `Sensör OUT → Pin ${ch.pin}, GND=GND, VCC=3.3V`
                        : `Röle IN → Pin ${ch.pin}, VCC=5V (harici besleme öneri), GND=GND${ch.active_low ? ' · Active LOW' : ' · Active HIGH'}`;
                      return (
                        <tr key={ch.id} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="p-2">
                            <div className="flex items-center gap-1.5">
                              <div className={`w-5 h-5 rounded ${meta.color} flex items-center justify-center`}>
                                <Icon className="h-3 w-3" />
                              </div>
                              <b>{ch.label || meta.label}</b>
                              {ch.dht_type && <span className="text-[10px] text-slate-500">({ch.dht_type})</span>}
                            </div>
                          </td>
                          <td className="p-2 font-mono font-bold text-emerald-700">{ch.pin}</td>
                          <td className="p-2 text-[10px] text-slate-500">{pinDetail}</td>
                          <td className="p-2 text-[10px] text-slate-600 dark:text-slate-300 hidden sm:table-cell">{wire}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-slate-500 space-y-1">
                <p>📌 <b>Kütüphane gereksinimleri:</b> {device.board_type === 'arduino_uno' ? 'Ethernet (built-in), SPI (built-in)' : (isESP32 ? 'WiFi, HTTPClient' : 'ESP8266WiFi, ESP8266HTTPClient')}, DHT (Adafruit), OneWire, DallasTemperature (DS18B20 için)</p>
                <p>⚙️ Cihaz her <b>3 sn</b>'de bir komut sorgular (röleyi en geç 3-5 sn içinde açar/kapatır), telemetri gönderimi ise <b>{Math.max(15, Math.min(60, Math.round((device.threshold_seconds || 180) / 3)))} sn</b> aralıklıdır.</p>
                {isHttps && device.board_type !== 'arduino_uno' && <p className="text-amber-600">⚠️ HTTPS: Kod içinde <code>setInsecure()</code> hazır — production için sertifika fingerprint eklemeniz önerilir.</p>}
                {isHttps && device.board_type === 'arduino_uno' && <p className="text-amber-600">⚠️ Arduino UNO HTTPS'yi doğrudan desteklemez. Lokal ağda HTTP endpoint kullanmanız veya reverse proxy üzerinden HTTP'den HTTPS'e geçmeniz gerekir.</p>}
              </div>
            </div>
          );
        })()}

        <div className="flex justify-end pt-2 border-t border-slate-100 dark:border-slate-700">
          <Button variant="outline" onClick={onClose}>Kapat</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Ayarlar Sekmesi (Kanal Düzenleme) --------------------
function SettingsTab({ device, setDevice, onSaved, token }) {
  const [channels, setChannels] = useState(device.channels || []);
  const [name, setName] = useState(device.name || '');
  const [location, setLocation] = useState(device.location || '');
  const [threshold, setThreshold] = useState(device.threshold_seconds || 180);
  const [cameraUrl, setCameraUrl] = useState(device.camera_url || '');
  const [cameraType, setCameraType] = useState(device.camera_type || 'snapshot');
  const [cameraLabel, setCameraLabel] = useState(device.camera_label || '');
  const [cameraVendor, setCameraVendor] = useState(device.camera_vendor || 'hikvision');
  const [cameraIp, setCameraIp] = useState(device.camera_ip || '');
  const [cameraUser, setCameraUser] = useState(device.camera_user || '');
  const [cameraPass, setCameraPass] = useState('');  // boş bırakılırsa eski parola korunur
  const [cameraChannel, setCameraChannel] = useState(device.camera_channel || 1);
  const [cameraSubstream, setCameraSubstream] = useState(!!device.camera_substream);
  const [doorRelayId, setDoorRelayId] = useState(device.door_relay_id || '');
  const [doorPulseMs, setDoorPulseMs] = useState(device.door_pulse_ms || 3000);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const updateChannel = (idx, patch) => setChannels(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  const removeChannel = (idx) => setChannels(prev => prev.filter((_, i) => i !== idx));
  const addChannel = (direction, kind) => {
    const meta = KIND_META[kind];
    const usedPins = channels.filter(c => c.direction === direction).map(c => c.pin);
    const pin = suggestPin(device.board_type, kind, direction, usedPins);
    setChannels(prev => [...prev, {
      id: Math.random().toString(36).slice(2, 10),
      pin, direction, kind,
      label: meta?.label || '',
      unit: meta?.unit || '',
      dht_type: (kind === 'temp' || kind === 'humidity') ? 'DHT22' : '',
      active_low: kind === 'relay',
    }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/smart/devices/${device.id}`, {
        method: 'PUT', headers, body: JSON.stringify({
          name, location, threshold_seconds: threshold, channels,
          camera_url: cameraUrl, camera_type: cameraType, camera_label: cameraLabel,
          camera_vendor: cameraVendor, camera_ip: cameraIp,
          camera_user: cameraUser,
          ...(cameraPass ? { camera_pass: cameraPass } : {}),
          camera_channel: Number(cameraChannel) || 1,
          camera_substream: !!cameraSubstream,
          door_relay_id: doorRelayId, door_pulse_ms: Number(doorPulseMs) || 3000,
        })
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Kaydedilemedi'); }
      const updated = await r.json();
      setDevice(updated);
      toast.success('Ayarlar kaydedildi');
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Cihaz Adı</Label>
          <Input value={name} onChange={e => setName(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs">Konum</Label>
          <Input value={location} onChange={e => setLocation(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs">Eşik (sn)</Label>
          <Input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} className="h-8 text-xs" />
        </div>
      </div>

      <div className="border rounded-lg p-2 bg-slate-50 dark:bg-slate-800/40">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold">Kanallar ({channels.length}) — pinleri düzenleyebilirsiniz</Label>
          <div className="flex flex-wrap gap-1">
            <Button size="sm" variant="outline" onClick={() => addChannel('in', 'temp')} className="h-7 text-xs"><Thermometer className="h-3 w-3 mr-1" />DHT</Button>
            <Button size="sm" variant="outline" onClick={() => addChannel('in', 'temp_ds18b20')} className="h-7 text-xs">DS18B20</Button>
            <Button size="sm" variant="outline" onClick={() => addChannel('in', 'motion')} className="h-7 text-xs">PIR</Button>
            <Button size="sm" variant="outline" onClick={() => addChannel('in', 'analog')} className="h-7 text-xs">Analog</Button>
            <Button size="sm" variant="outline" onClick={() => addChannel('out', 'relay')} className="h-7 text-xs text-emerald-700"><Power className="h-3 w-3 mr-1" />Röle</Button>
          </div>
        </div>
        {channels.length === 0 ? (
          <p className="text-center text-xs text-slate-400 py-4">Henüz kanal yok. Yukarıdaki butonlarla ekleyin.</p>
        ) : (
          <div className="space-y-1">
            {channels.map((ch, i) => {
              const meta = KIND_META[ch.kind] || {};
              const Icon = meta.icon || Radio;
              const isDHT = ch.kind === 'temp' || ch.kind === 'humidity';
              const isRelay = ch.kind === 'relay';
              const pinOptions = device.board_type === 'esp32'
                ? ['2', '4', '5', '13', '14', '25', '26', '27', '32', '33', '34', '35']
                : device.board_type === 'esp32s3'
                ? ['1', '2', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '21', '38', '39', '40', '41', '42']
                : device.board_type === 'arduino_uno'
                ? ['2', '3', '5', '6', '7', '8', '9', 'A0', 'A1', 'A2', 'A3']
                : ['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8', 'A0'];
              return (
                <div key={i} className="bg-white dark:bg-slate-900 rounded border p-2 space-y-1">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className={`col-span-1 w-6 h-6 rounded ${meta.color || ''} flex items-center justify-center`}><Icon className="h-3 w-3" /></div>
                    <Input className="col-span-4 h-7 text-xs" value={ch.label} onChange={e => updateChannel(i, { label: e.target.value })} placeholder="Etiket" data-testid={`ch-label-${i}`} />
                    <Select value={ch.pin} onValueChange={v => updateChannel(i, { pin: v })}>
                      <SelectTrigger className="col-span-2 h-7 text-xs font-mono font-bold" data-testid={`ch-pin-${i}`}><SelectValue placeholder="Pin" /></SelectTrigger>
                      <SelectContent>
                        {pinOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="col-span-4 text-[10px] text-slate-500">{ch.direction === 'in' ? '📥 ' : '📤 '}{meta.label}</div>
                    <Button size="icon" variant="ghost" className="col-span-1 h-7 w-7 text-red-500" onClick={() => removeChannel(i)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                  {(isDHT || isRelay) && (
                    <div className="grid grid-cols-12 gap-2 items-center pl-8">
                      {isDHT && (
                        <>
                          <span className="col-span-2 text-[10px] text-slate-500">DHT:</span>
                          <Select value={ch.dht_type || 'DHT22'} onValueChange={v => updateChannel(i, { dht_type: v })}>
                            <SelectTrigger className="col-span-3 h-6 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="DHT11">DHT11 (mavi)</SelectItem>
                              <SelectItem value="DHT22">DHT22 (beyaz)</SelectItem>
                            </SelectContent>
                          </Select>
                        </>
                      )}
                      {isRelay && (
                        <>
                          <span className="col-span-2 text-[10px] text-slate-500">Röle:</span>
                          <Select value={ch.active_low ? 'low' : 'high'} onValueChange={v => updateChannel(i, { active_low: v === 'low' })}>
                            <SelectTrigger className="col-span-4 h-6 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Active LOW (yaygın)</SelectItem>
                              <SelectItem value="high">Active HIGH</SelectItem>
                            </SelectContent>
                          </Select>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Kamera & Kapı Rölesi Ayarları */}
      <div className="border rounded-lg p-3 bg-indigo-50/40 dark:bg-indigo-900/10 space-y-2" data-testid="camera-settings">
        <Label className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-1">📹 IP Kamera & Kapı Kontrolü (opsiyonel)</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Üretici / Protokol</Label>
            <Select value={cameraVendor} onValueChange={setCameraVendor}>
              <SelectTrigger className="h-8 text-xs" data-testid="camera-vendor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hikvision">Hikvision (ISAPI)</SelectItem>
                <SelectItem value="onvif">ONVIF (snapshot)</SelectItem>
                <SelectItem value="generic">Manuel URL (Android IPCam vs.)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Yayın Tipi</Label>
            <Select value={cameraType} onValueChange={setCameraType}>
              <SelectTrigger className="h-8 text-xs" data-testid="camera-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="snapshot">Snapshot (sn'de 1 kare, hafif)</SelectItem>
                <SelectItem value="mjpeg">MJPEG stream (akıcı video)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kamera Etiketi</Label>
            <Input value={cameraLabel} onChange={e => setCameraLabel(e.target.value)} placeholder="Kapı Kamerası" className="h-8 text-xs" />
          </div>
        </div>

        {cameraVendor !== 'generic' ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 border-t border-indigo-200/60 dark:border-indigo-700/40 pt-2 mt-1">
            <div className="md:col-span-2">
              <Label className="text-xs">Kamera IP / Hostname</Label>
              <Input value={cameraIp} onChange={e => setCameraIp(e.target.value)} placeholder="192.168.1.64 veya kamera.evim.net:8080" className="h-8 text-xs font-mono" data-testid="camera-ip" />
              <p className="text-[10px] text-slate-500 mt-0.5">
                İçeriden erişim için yerel IP yeterli. Dışarıdan erişim için <b>DynDNS/public IP:port</b> gerekli (yönlendirici port forward).
              </p>
            </div>
            <div>
              <Label className="text-xs">Kullanıcı Adı</Label>
              <Input value={cameraUser} onChange={e => setCameraUser(e.target.value)} placeholder="admin" className="h-8 text-xs" autoComplete="off" data-testid="camera-user" />
            </div>
            <div>
              <Label className="text-xs">Parola</Label>
              <Input type="password" value={cameraPass} onChange={e => setCameraPass(e.target.value)} placeholder={device.camera_user ? '(değişiklik yoksa boş bırakın)' : 'Kamera parolası'} className="h-8 text-xs" autoComplete="new-password" data-testid="camera-pass" />
            </div>
            <div>
              <Label className="text-xs">Kanal No</Label>
              <Input type="number" min="1" max="99" value={cameraChannel} onChange={e => setCameraChannel(e.target.value)} className="h-8 text-xs" data-testid="camera-channel" />
            </div>
            <div className="md:col-span-3 flex items-center gap-2">
              <Switch checked={cameraSubstream} onCheckedChange={setCameraSubstream} data-testid="camera-substream" />
              <span className="text-xs text-slate-600 dark:text-slate-300">Alt akış kullan (düşük çözünürlük — bant genişliği tasarrufu)</span>
            </div>
          </div>
        ) : (
          <div className="border-t border-indigo-200/60 dark:border-indigo-700/40 pt-2 mt-1">
            <Label className="text-xs">Kamera URL</Label>
            <Input value={cameraUrl} onChange={e => setCameraUrl(e.target.value)} placeholder="http://192.168.1.100:8080/video (Android IPCam)" className="h-8 text-xs font-mono" data-testid="camera-url" />
            <p className="text-[10px] text-slate-500 mt-0.5">
              Örnek: Android <b>IP Webcam</b> uygulamasının MJPEG çıkışı: <code>http://192.168.1.x:8080/video</code>, snapshot: <code>http://192.168.1.x:8080/photo.jpg</code>
            </p>
          </div>
        )}

        {cameraVendor !== 'generic' && (
          <div className="flex items-center justify-between gap-2 pt-1">
            <p className="text-[10px] text-slate-500">
              💡 Sunucu kameraya doğrudan bağlanır (kullanıcı/parola veritabanında şifreli). Panelden kameraya giden parola tarayıcıya hiç gönderilmez.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={testing || !cameraIp}
              data-testid="camera-test-btn"
              onClick={async () => {
                setTesting(true);
                try {
                  // Önce kaydet, sonra test et
                  const r = await fetch(`${API}/smart/devices/${device.id}`, { method: 'PUT', headers,
                    body: JSON.stringify({
                      camera_vendor: cameraVendor, camera_ip: cameraIp, camera_user: cameraUser,
                      ...(cameraPass ? { camera_pass: cameraPass } : {}),
                      camera_channel: Number(cameraChannel) || 1, camera_substream: !!cameraSubstream,
                    }),
                  });
                  if (!r.ok) throw new Error('Kayıt başarısız');
                  const testR = await fetch(`${API}/smart/devices/${device.id}/camera-snapshot?_t=${Date.now()}`, { headers: { Authorization: `Bearer ${token}` } });
                  if (testR.ok) toast.success('✅ Kameraya bağlandı! Dashboard\'da görüntü hazır.');
                  else {
                    const d = await testR.json().catch(() => ({}));
                    toast.error(`❌ ${d.detail || 'Bağlanılamadı'}`);
                  }
                } catch (e) { toast.error(e.message); }
                finally { setTesting(false); }
              }}
            >
              {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : '🔌 Bağlantıyı Test Et'}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-indigo-200/60 dark:border-indigo-700/40 pt-2 mt-1">
          <div>
            <Label className="text-xs">Kapı Rölesi</Label>
            <Select value={doorRelayId || 'none'} onValueChange={v => setDoorRelayId(v === 'none' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="door-relay-select"><SelectValue placeholder="Seçili değil" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Yok —</SelectItem>
                {channels.filter(c => c.direction === 'out').map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.label || 'Röle'} (Pin {c.pin})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kapı açılma süresi (ms)</Label>
            <Input type="number" min="500" max="10000" step="500" value={doorPulseMs} onChange={e => setDoorPulseMs(e.target.value)} className="h-8 text-xs" />
            <p className="text-[10px] text-slate-500 mt-0.5">Kapıyı Aç butonuna basınca röle bu süre kadar açık kalır (manyetik kilit için 1-5 sn önerilir).</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Ayarları Kaydet</>}
        </Button>
      </div>
    </div>
  );
}

// -------------------- Arduino Kod Üreteci --------------------
function generateArduinoCode(device, { telemetryUrl, pollUrl, ackUrl, isHttps, isESP32, boardType }) {
  const sensors = (device.channels || []).filter(c => c.direction === 'in');
  const relays = (device.channels || []).filter(c => c.direction === 'out');

  // ----- Arduino UNO + Ethernet Shield için ayrı kod jeneratörü -----
  if (boardType === 'arduino_uno') {
    return generateArduinoUnoCode(device, { telemetryUrl, pollUrl, ackUrl, isHttps });
  }

  const dhtSensor = sensors.find(s => s.kind === 'temp' || s.kind === 'humidity');
  const hasDHT = !!dhtSensor;
  const dhtType = (dhtSensor?.dht_type || 'DHT22');
  const hasDS18 = sensors.some(s => s.kind === 'temp_ds18b20');
  const defaultSensorPin = boardType === 'esp8266' ? 'D4' : (boardType === 'esp32s3' ? '4' : '4');
  const defaultDS18Pin = boardType === 'esp8266' ? 'D3' : (boardType === 'esp32s3' ? '2' : '15');
  const defaultRelayPin = boardType === 'esp8266' ? 'D1' : (boardType === 'esp32s3' ? '5' : '5');
  const defaultMotionPin = boardType === 'esp8266' ? 'D5' : (boardType === 'esp32s3' ? '6' : '14');
  const dhtPin = dhtSensor?.pin || defaultSensorPin;
  const ds18Pin = sensors.find(s => s.kind === 'temp_ds18b20')?.pin || defaultDS18Pin;
  const tempChId = sensors.find(s => s.kind === 'temp')?.id;
  const humChId = sensors.find(s => s.kind === 'humidity')?.id;

  const wifiInc = isESP32 ? '#include <WiFi.h>\n#include <HTTPClient.h>' : '#include <ESP8266WiFi.h>\n#include <ESP8266HTTPClient.h>';
  const secureInc = isHttps ? (isESP32 ? '\n#include <WiFiClientSecure.h>' : '\n#include <WiFiClientSecureBearSSL.h>') : (isESP32 ? '' : '\n#include <WiFiClient.h>');
  const dhtInc = hasDHT ? '\n#include <DHT.h>' : '';
  const ds18Inc = hasDS18 ? '\n#include <OneWire.h>\n#include <DallasTemperature.h>' : '';

  const clientSetup = isHttps
    ? (isESP32
       ? '  WiFiClientSecure client;\n  client.setInsecure();\n  HTTPClient http;\n  http.begin(client, url);'
       : '  std::unique_ptr<BearSSL::WiFiClientSecure> client(new BearSSL::WiFiClientSecure);\n  client->setInsecure();\n  HTTPClient http;\n  http.begin(*client, url);')
    : (isESP32
       ? '  HTTPClient http;\n  http.begin(url);'
       : '  WiFiClient client;\n  HTTPClient http;\n  http.begin(client, url);');

  const relayDefs = relays.map(r => `  { "${r.id}", ${r.pin || defaultRelayPin}, ${r.active_low ? 'true' : 'false'} }`).join(',\n');

  const dhtRead = hasDHT ? `
  // DHT sensör okuma
  float t = dht.readTemperature();
  float h = dht.readHumidity();
  ${tempChId ? `if (!isnan(t)) body += String("\\"${tempChId}\\":") + t + ",";` : ''}
  ${humChId ? `if (!isnan(h)) body += String("\\"${humChId}\\":") + h + ",";` : ''}` : '';
  const ds18Read = hasDS18 ? `
  // DS18B20 sensör okuma
  ds18.requestTemperatures();
  float dsT = ds18.getTempCByIndex(0);
  if (dsT > -100) body += String("\\"${sensors.find(s => s.kind === 'temp_ds18b20')?.id || ''}\\":") + dsT + ",";` : '';
  const analogRead = sensors.filter(s => s.kind === 'analog').map(s => `
  int a_${s.id} = analogRead(${s.pin || 'A0'});
  body += String("\\"${s.id}\\":") + a_${s.id} + ",";`).join('');
  const motionRead = sensors.filter(s => s.kind === 'motion').map(s => `
  int m_${s.id} = digitalRead(${s.pin || defaultMotionPin});
  body += String("\\"${s.id}\\":") + m_${s.id} + ",";`).join('');

  const bLabel = boardType === 'esp32s3' ? 'ESP32-S3' : (boardType === 'esp32' ? 'ESP32' : 'ESP8266 / NodeMCU');

  // Fiziksel bağlantı yorumu (kod başında)
  const wiringComment = `/*
 * ==========================================
 * FİZİKSEL BAĞLANTI ŞEMASI (${bLabel})
 * ==========================================
${sensors.map(s => ` * SENSÖR: ${s.label || KIND_META[s.kind]?.label} → Pin ${s.pin}${s.kind === 'temp' || s.kind === 'humidity' ? ` (${s.dht_type || 'DHT22'})` : ''}`).join('\n')}
${relays.map(r => ` * RÖLE : ${r.label} → Pin ${r.pin} (${r.active_low ? 'Active LOW - yaygın' : 'Active HIGH'})`).join('\n')}
 *
 * VCC bağlantıları:
 *  - DHT modülü: VCC → 3.3V veya 5V (modül tipine göre), GND → GND, DATA → yukarıdaki pin
 *  - Röle kartı: VCC → 5V (harici besleme önerilir!), GND → GND, IN → yukarıdaki pin
 *  - PIR HC-SR501: VCC → 5V, GND → GND, OUT → pin
${boardType === 'esp32s3' ? ' *  - ESP32-S3: 3.3V lojik seviye. Röle kartları için 5V tolerant modül önerilir (lojik seviye dönüştürücü eklenmesi gerekebilir).\n' : ''} * ==========================================
 */
`;

  const poll_interval_ms = 3000; // 3 saniyede bir komut sorgulama
  const telem_interval_ms = Math.max(15000, Math.min(60000, Math.round(((device.threshold_seconds || 180) * 1000) / 3))); // sinyal eşiğinin 1/3'ü, en az 15 sn

  return `${wiringComment}${wifiInc}${secureInc}${dhtInc}${ds18Inc}

const char* WIFI_SSID = "wifi_adiniz";
const char* WIFI_PASS = "wifi_sifresi";
const char* API_KEY   = "${device.api_key}";
const char* TELEMETRY_URL = "${telemetryUrl}";
const char* POLL_URL      = "${pollUrl}";
const char* ACK_URL       = "${ackUrl}";
// Hızlı röle tepkisi için ayrı aralıklar:
const unsigned long POLL_INTERVAL_MS      = ${poll_interval_ms}UL;   // komut sorgu (röle tepkisi)
const unsigned long TELEMETRY_INTERVAL_MS = ${telem_interval_ms}UL;  // sensör gönderimi

${hasDHT ? `#define DHT_PIN ${dhtPin}\n#define DHT_TYPE ${dhtType}\nDHT dht(DHT_PIN, DHT_TYPE);\n` : ''}${hasDS18 ? `#define ONE_WIRE_BUS ${ds18Pin}\nOneWire oneWire(ONE_WIRE_BUS);\nDallasTemperature ds18(&oneWire);\n` : ''}
// Röle pin haritası: {channel_id, pin, active_low}
struct Relay { const char* id; int pin; bool activeLow; };
Relay relays[] = {
${relayDefs || '  // röle yok'}
};
const int RELAY_COUNT = sizeof(relays) / sizeof(relays[0]);

inline void writeRelay(int i, bool on) {
  digitalWrite(relays[i].pin, (on ^ relays[i].activeLow) ? HIGH : LOW);
}

unsigned long lastTelemetry = 0;
unsigned long lastPoll = 0;

void setup() {
  Serial.begin(115200);
  ${hasDHT ? 'dht.begin();' : ''}
  ${hasDS18 ? 'ds18.begin();' : ''}
  for (int i = 0; i < RELAY_COUNT; i++) {
    pinMode(relays[i].pin, OUTPUT);
    writeRelay(i, false);
  }
  ${sensors.filter(s => s.kind === 'motion').map(s => `pinMode(${s.pin || defaultMotionPin}, INPUT);`).join('\n  ')}
  WiFi.begin(WIFI_SSID, WIFI_PASS);
  while (WiFi.status() != WL_CONNECTED) { delay(500); Serial.print("."); }
  Serial.println("\\nBaglandi: " + WiFi.localIP().toString());
}

void sendTelemetry() {
  if (WiFi.status() != WL_CONNECTED) return;
  String body = "{\\"api_key\\":\\"" + String(API_KEY) + "\\",\\"readings\\":{";${dhtRead}${ds18Read}${analogRead}${motionRead}
  if (body.endsWith(",")) body.remove(body.length() - 1);
  body += "}}";
  String url = TELEMETRY_URL;
${clientSetup}
  http.addHeader("Content-Type", "application/json");
  int code = http.POST(body);
  Serial.printf("Telemetry => %d\\n", code);
  http.end();
}

void pollCommands() {
  if (WiFi.status() != WL_CONNECTED) return;
  String url = POLL_URL;
${clientSetup}
  int code = http.GET();
  if (code == 200) {
    String payload = http.getString();
    String ackedIds = "";
    int idx = 0;
    while ((idx = payload.indexOf("\\"channel_id\\":\\"", idx)) != -1) {
      idx += 15;
      int end = payload.indexOf("\\"", idx);
      String chId = payload.substring(idx, end);
      int actIdx = payload.indexOf("\\"action\\":\\"", end);
      String action = payload.substring(actIdx + 10, payload.indexOf("\\"", actIdx + 10));
      int cmdIdx = payload.lastIndexOf("\\"id\\":\\"", actIdx);
      String cmdId = payload.substring(cmdIdx + 6, payload.indexOf("\\"", cmdIdx + 6));
      // pulse_delay_ms varsa, OFF komutunu geciktir (kapı açma kısayolu için)
      long pulseDelay = 0;
      int pdIdx = payload.indexOf("\\"pulse_delay_ms\\":", end);
      if (pdIdx > 0 && pdIdx < payload.indexOf("}", end)) {
        pulseDelay = payload.substring(pdIdx + 17, payload.indexOf(",", pdIdx + 17)).toInt();
      }
      for (int i = 0; i < RELAY_COUNT; i++) {
        if (chId == relays[i].id) {
          if (pulseDelay > 0) delay(pulseDelay);
          writeRelay(i, action == "on");
          Serial.printf("Rele %s => %s\\n", chId.c_str(), action.c_str());
        }
      }
      ackedIds += (ackedIds.length() > 0 ? "," : "") + String("\\"") + cmdId + "\\"";
    }
    if (ackedIds.length() > 0) {
      http.end();
      String ackUrl = ACK_URL;
${clientSetup.replace(/url/g, 'ackUrl')}
      http.addHeader("Content-Type", "application/json");
      String ackBody = "{\\"api_key\\":\\"" + String(API_KEY) + "\\",\\"command_ids\\":[" + ackedIds + "]}";
      http.POST(ackBody);
    }
  }
  http.end();
}

void loop() {
  unsigned long now = millis();
  if (now - lastPoll >= POLL_INTERVAL_MS) { lastPoll = now; pollCommands(); }
  if (now - lastTelemetry >= TELEMETRY_INTERVAL_MS) { lastTelemetry = now; sendTelemetry(); }
  delay(100);
}
`;
}


// -------------------- Arduino UNO + Ethernet Shield Kod Üreteci --------------------
function generateArduinoUnoCode(device, { telemetryUrl, pollUrl, ackUrl, isHttps }) {
  const sensors = (device.channels || []).filter(c => c.direction === 'in');
  const relays = (device.channels || []).filter(c => c.direction === 'out');
  const dhtSensor = sensors.find(s => s.kind === 'temp' || s.kind === 'humidity');
  const hasDHT = !!dhtSensor;
  const dhtType = (dhtSensor?.dht_type || 'DHT22');
  const hasDS18 = sensors.some(s => s.kind === 'temp_ds18b20');
  const dhtPin = dhtSensor?.pin || '2';
  const ds18Pin = sensors.find(s => s.kind === 'temp_ds18b20')?.pin || '3';
  const tempChId = sensors.find(s => s.kind === 'temp')?.id;
  const humChId = sensors.find(s => s.kind === 'humidity')?.id;

  // telemetry URL'ini host + path'e ayır (Arduino EthernetClient manuel HTTP yazımı)
  let host = '', path = '/', port = 80;
  try {
    const u = new URL(telemetryUrl);
    host = u.hostname;
    port = u.port ? Number(u.port) : (u.protocol === 'https:' ? 443 : 80);
    path = u.pathname + (u.search || '');
  } catch { /* ignore */ }

  const pollUrlObj = (() => { try { const u = new URL(pollUrl); return { host: u.hostname, port: u.port ? Number(u.port) : 80, path: u.pathname + u.search }; } catch { return { host, port, path: '/' }; } })();
  const ackUrlObj  = (() => { try { const u = new URL(ackUrl);  return { host: u.hostname, port: u.port ? Number(u.port) : 80, path: u.pathname + u.search }; } catch { return { host, port, path: '/' }; } })();

  const relayDefs = relays.map(r => `  { "${r.id}", ${r.pin || '7'}, ${r.active_low ? 'true' : 'false'} }`).join(',\n');
  const motionPinSetup = sensors.filter(s => s.kind === 'motion').map(s => `  pinMode(${s.pin || '8'}, INPUT);`).join('\n');

  const dhtRead = hasDHT ? `
  // DHT sensör okuma
  float t = dht.readTemperature();
  float h = dht.readHumidity();
  ${tempChId ? `if (!isnan(t)) { body += F("\\"${tempChId}\\":"); body += String(t, 1); body += F(","); }` : ''}
  ${humChId  ? `if (!isnan(h)) { body += F("\\"${humChId}\\":"); body += String(h, 1); body += F(","); }` : ''}` : '';
  const ds18Read = hasDS18 ? `
  // DS18B20 okuma
  ds18.requestTemperatures();
  float dsT = ds18.getTempCByIndex(0);
  if (dsT > -100) { body += F("\\"${sensors.find(s => s.kind === 'temp_ds18b20')?.id || ''}\\":"); body += String(dsT, 1); body += F(","); }` : '';
  const analogRead = sensors.filter(s => s.kind === 'analog').map(s => `
  { int v = analogRead(${s.pin || 'A0'}); body += F("\\"${s.id}\\":"); body += v; body += F(","); }`).join('');
  const motionRead = sensors.filter(s => s.kind === 'motion').map(s => `
  { int v = digitalRead(${s.pin || '8'}); body += F("\\"${s.id}\\":"); body += v; body += F(","); }`).join('');

  const poll_interval_ms = 3000;
  const telem_interval_ms = Math.max(15000, Math.min(60000, Math.round(((device.threshold_seconds || 180) * 1000) / 3)));

  const wiringComment = `/*
 * ==========================================
 * FİZİKSEL BAĞLANTI ŞEMASI (Arduino UNO + Ethernet Shield W5100/W5500)
 * ==========================================
 * ÖNEMLİ:
 *  - Ethernet Shield pini 10, 11, 12, 13 (SPI) ve 4 (SD CS) kullanır — bu pinleri sensör/röle için kullanmayın
 *  - Shield doğrudan UNO üzerine takılır, LAN kablosu ile router'a bağlanır (DHCP otomatik IP alır)
 *  - 5V ve GND beslemesi UNO'nun kendi pinlerinden alınabilir, röle kartı için harici 5V önerilir
 *
${sensors.map(s => ` * SENSÖR: ${s.label || KIND_META[s.kind]?.label} → Pin ${s.pin}${s.kind === 'temp' || s.kind === 'humidity' ? ` (${s.dht_type || 'DHT22'})` : ''}`).join('\n')}
${relays.map(r => ` * RÖLE : ${r.label} → Pin ${r.pin} (${r.active_low ? 'Active LOW' : 'Active HIGH'})`).join('\n')}
 *
 * KÜTÜPHANELER: Ethernet (built-in), SPI (built-in)${hasDHT ? ', DHT sensor library (Adafruit)' : ''}${hasDS18 ? ', OneWire, DallasTemperature' : ''}
 * ==========================================
 */
`;

  return `${wiringComment}#include <SPI.h>
#include <Ethernet.h>${hasDHT ? '\n#include <DHT.h>' : ''}${hasDS18 ? '\n#include <OneWire.h>\n#include <DallasTemperature.h>' : ''}

// Shield MAC adresi — kendi shield'inizin etiketindeki MAC adresini yazın (her shield farklı)
byte mac[] = { 0xDE, 0xAD, 0xBE, 0xEF, 0xFE, 0xED };

// Sunucu bilgisi
const char* API_KEY     = "${device.api_key}";
const char* HOST_NAME   = "${host}";
const int   HOST_PORT   = ${port};
const char* TELEM_PATH  = "${path}";
const char* POLL_PATH   = "${pollUrlObj.path}";
const char* ACK_PATH    = "${ackUrlObj.path}";
${isHttps ? '// NOT: Arduino UNO HTTPS\'yi doğrudan desteklemez — sunucunuzda HTTP endpoint kullanın\n// veya reverse proxy ile iç ağdan HTTP erişim sağlayın\n' : ''}
// Hızlı röle tepkisi için ayrı aralıklar:
const unsigned long POLL_INTERVAL_MS      = ${poll_interval_ms}UL;
const unsigned long TELEMETRY_INTERVAL_MS = ${telem_interval_ms}UL;

${hasDHT ? `#define DHT_PIN ${dhtPin}\n#define DHT_TYPE ${dhtType}\nDHT dht(DHT_PIN, DHT_TYPE);\n` : ''}${hasDS18 ? `#define ONE_WIRE_BUS ${ds18Pin}\nOneWire oneWire(ONE_WIRE_BUS);\nDallasTemperature ds18(&oneWire);\n` : ''}
// Röle pin haritası: {channel_id, pin, active_low}
struct Relay { const char* id; int pin; bool activeLow; };
Relay relays[] = {
${relayDefs || '  { "", 0, false }'}
};
const int RELAY_COUNT = ${relays.length};

inline void writeRelay(int i, bool on) {
  digitalWrite(relays[i].pin, (on ^ relays[i].activeLow) ? HIGH : LOW);
}

EthernetClient netClient;
unsigned long lastTelemetry = 0;
unsigned long lastPoll = 0;

void setup() {
  Serial.begin(9600);
  Serial.println(F("Baslatiliyor..."));
  ${hasDHT ? 'dht.begin();' : ''}
  ${hasDS18 ? 'ds18.begin();' : ''}
  for (int i = 0; i < RELAY_COUNT; i++) {
    pinMode(relays[i].pin, OUTPUT);
    writeRelay(i, false);
  }
${motionPinSetup}
  // Ethernet başlat (DHCP)
  if (Ethernet.begin(mac) == 0) {
    Serial.println(F("DHCP basarisiz!"));
    // Statik IP ile deneyin: Ethernet.begin(mac, IPAddress(192,168,1,177));
    while (true) delay(1000);
  }
  delay(1000);
  Serial.print(F("IP: ")); Serial.println(Ethernet.localIP());
}

// Basit HTTP POST — gövde JSON
bool httpPost(const char* path, const String &body) {
  netClient.stop();
  if (!netClient.connect(HOST_NAME, HOST_PORT)) {
    Serial.println(F("Baglanti HATA"));
    return false;
  }
  netClient.print(F("POST ")); netClient.print(path); netClient.println(F(" HTTP/1.1"));
  netClient.print(F("Host: ")); netClient.println(HOST_NAME);
  netClient.println(F("Content-Type: application/json"));
  netClient.print(F("Content-Length: ")); netClient.println(body.length());
  netClient.println(F("Connection: close"));
  netClient.println();
  netClient.print(body);
  // Cevabı oku ve at
  unsigned long t = millis();
  while (netClient.connected() && (millis() - t) < 3000) {
    while (netClient.available()) { netClient.read(); t = millis(); }
  }
  netClient.stop();
  return true;
}

// Basit HTTP GET — gelen gövdeyi döndürür
String httpGet(const char* path) {
  netClient.stop();
  String resp = "";
  if (!netClient.connect(HOST_NAME, HOST_PORT)) return resp;
  netClient.print(F("GET ")); netClient.print(path); netClient.println(F(" HTTP/1.1"));
  netClient.print(F("Host: ")); netClient.println(HOST_NAME);
  netClient.println(F("Connection: close"));
  netClient.println();
  unsigned long t = millis();
  bool inBody = false;
  String line = "";
  while (netClient.connected() && (millis() - t) < 4000) {
    while (netClient.available()) {
      char c = netClient.read(); t = millis();
      if (!inBody) {
        line += c;
        if (line.endsWith("\\r\\n\\r\\n")) { inBody = true; line = ""; }
        else if (line.length() > 300) line = "";
      } else {
        resp += c;
        if (resp.length() > 450) break;  // UNO belleği sınırlı
      }
    }
    if (resp.length() > 450) break;
  }
  netClient.stop();
  return resp;
}

void sendTelemetry() {
  String body;
  body.reserve(180);
  body = F("{\\"api_key\\":\\"");
  body += API_KEY;
  body += F("\\",\\"readings\\":{");${dhtRead}${ds18Read}${analogRead}${motionRead}
  if (body.endsWith(",")) body.remove(body.length() - 1);
  body += F("}}");
  Serial.print(F("TX: ")); Serial.println(body);
  httpPost(TELEM_PATH, body);
}

void pollCommands() {
  String payload = httpGet(POLL_PATH);
  if (payload.length() == 0) return;
  Serial.print(F("RX: ")); Serial.println(payload);
  String ackedIds = "";
  int idx = 0;
  while ((idx = payload.indexOf("\\"channel_id\\":\\"", idx)) != -1) {
    idx += 15;
    int end = payload.indexOf("\\"", idx);
    String chId = payload.substring(idx, end);
    int actIdx = payload.indexOf("\\"action\\":\\"", end);
    String action = payload.substring(actIdx + 10, payload.indexOf("\\"", actIdx + 10));
    int cmdIdx = payload.lastIndexOf("\\"id\\":\\"", actIdx);
    String cmdId = payload.substring(cmdIdx + 6, payload.indexOf("\\"", cmdIdx + 6));
    long pulseDelay = 0;
    int pdIdx = payload.indexOf("\\"pulse_delay_ms\\":", end);
    if (pdIdx > 0 && pdIdx < payload.indexOf("}", end)) {
      pulseDelay = payload.substring(pdIdx + 17, payload.indexOf(",", pdIdx + 17)).toInt();
    }
    for (int i = 0; i < RELAY_COUNT; i++) {
      if (chId == relays[i].id) {
        if (pulseDelay > 0) delay(pulseDelay);
        writeRelay(i, action == "on");
        Serial.print(F("Rele ")); Serial.print(chId); Serial.print(F(" => ")); Serial.println(action);
      }
    }
    ackedIds += (ackedIds.length() > 0 ? "," : "");
    ackedIds += "\\"";
    ackedIds += cmdId;
    ackedIds += "\\"";
  }
  if (ackedIds.length() > 0) {
    String ackBody = F("{\\"api_key\\":\\"");
    ackBody += API_KEY;
    ackBody += F("\\",\\"command_ids\\":[");
    ackBody += ackedIds;
    ackBody += F("]}");
    httpPost(ACK_PATH, ackBody);
  }
}

void loop() {
  Ethernet.maintain();  // DHCP yenileme
  unsigned long now = millis();
  if (now - lastPoll >= POLL_INTERVAL_MS) { lastPoll = now; pollCommands(); }
  if (now - lastTelemetry >= TELEMETRY_INTERVAL_MS) { lastTelemetry = now; sendTelemetry(); }
  delay(100);
}
`;
}
